import { NavigateFunction } from 'react-router-dom';

export interface NavigationOptions {
  href: string;
  event: React.MouseEvent | React.KeyboardEvent;
  navigate: NavigateFunction;
  onBeforeNavigate?: () => void;
}

/**
 * Handles navigation with support for cmd/ctrl+click to open in new tab
 * Provides consistent navigation behavior across all clickable components
 * 
 * @param options - Navigation configuration
 * @param options.href - The URL to navigate to
 * @param options.event - The mouse or keyboard event that triggered navigation
 * @param options.navigate - React Router's navigate function
 * @param options.onBeforeNavigate - Optional callback to execute before navigation
 * @returns true if navigation was handled, false otherwise
 */
export const handleNavigation = ({
  href,
  event,
  navigate,
  onBeforeNavigate,
}: NavigationOptions): boolean => {
  // Handle keyboard events
  if ('key' in event) {
    const keyEvent = event as React.KeyboardEvent;
    if (keyEvent.key === 'Enter') {
      event.preventDefault();
      onBeforeNavigate?.();
      navigate(href);
      return true;
    }
    return false;
  }

  // Handle mouse events with cmd/ctrl+click
  const mouseEvent = event as React.MouseEvent;
  const shouldOpenInNewTab = mouseEvent.metaKey || mouseEvent.ctrlKey;
  
  // Handle cmd/ctrl+click - always open in new tab
  if (shouldOpenInNewTab) {
    event.preventDefault();
    onBeforeNavigate?.();
    window.open(href, '_blank', 'noopener,noreferrer');
    return true;
  }

  // Check if external link
  const isExternalLink = href.startsWith('http://') || href.startsWith('https://');
  
  if (isExternalLink) {
    event.preventDefault();
    onBeforeNavigate?.();
    window.open(href, '_blank', 'noopener,noreferrer');
    return true;
  }

  // Internal link - use React Router
  event.preventDefault();
  onBeforeNavigate?.();
  navigate(href);
  return true;
};

/**
 * Checks if a URL is an internal route
 * @param href - The URL to check
 * @returns true if the URL is an internal route, false otherwise
 */
export const isInternalRoute = (href: string): boolean => {
  return href.startsWith('/') && !href.startsWith('http://') && !href.startsWith('https://');
};
